/**
 * Shared AudioContext singleton — used by tradeAudio and defenseAudio.
 *
 * Single AudioContext avoids browser limits on concurrent contexts.
 */

let audioCtx: AudioContext | null = null;

export function getAudioContext(): AudioContext | null {
  try {
    if (!audioCtx) {
      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      audioCtx = new Ctor();
    }
    return audioCtx;
  } catch {
    return null;
  }
}

export async function playTone(freqHz: number, durationSec: number, amplitude: number): Promise<void> {
  const ctx = getAudioContext();
  if (!ctx) return;
  // Browsers start AudioContext in "suspended" state — must await resume before scheduling
  if (ctx.state === 'suspended') {
    await ctx.resume();
  }
  // Scale amplitude by master volume from localStorage
  let masterVolume = 0.5;
  try {
    const raw = localStorage.getItem('galactus:soundSettings');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (typeof parsed.volume === 'number') masterVolume = parsed.volume;
    }
  } catch { /* noop */ }
  const scaled = amplitude * masterVolume;
  if (scaled < 0.001) return; // effectively silent
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.frequency.value = freqHz;
  osc.type = 'sine';
  gain.gain.setValueAtTime(scaled, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + durationSec);
  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + durationSec);
}
